(function () {
  'use strict';
  function percentage(original, discounted, reported = 100) {
    if (!Number.isSafeInteger(original) || original <= 0 || !Number.isSafeInteger(discounted)
      || discounted < 0 || discounted >= original || !Number.isFinite(reported) || reported <= 0) return null;
    return Math.floor(Math.min((original - discounted) * 100 / original, reported));
  }
  function campaignLabel(applied, entries = []) {
    if (!applied?.promotionToken || !applied.offerToken || applied.discountCode) return '';
    const matches = entries.filter(entry => entry.campaignToken === applied.promotionToken
      && Array.isArray(entry.offerTokens) && entry.offerTokens.includes(applied.offerToken));
    if (matches.length !== 1 || typeof matches[0].label !== 'string') return '';
    const label = matches[0].label.trim();
    return Array.from(label).length <= 12 ? label : '';
  }
  function campaignEntries(doc) {
    return [...doc.querySelectorAll('#cg-campaign-labels [data-campaign-token]')].map(node => {
      try {
        return {campaignToken:node.dataset.campaignToken, offerTokens:JSON.parse(node.dataset.offerTokens), label:node.dataset.label};
      } catch (_) { return {}; }
    });
  }
  function pdpLabel(result, entitlements = [], entries = []) {
    // The caller confirms this variant's discounted PDP result. `applied` is
    // cart state and remains false for a valid coupon when the cart is empty.
    if (entitlements.some(item => item.offerToken === result.offerToken
      && item.source === 'native' && item.entitlement?.level === 'product')
      && typeof result.discountCode === 'string') return result.discountCode;
    // DN's PDP result can put its offer token in discountCode. The public
    // entitlement source distinguishes an app offer from a native coupon.
    return entitlements.some(item => item.offerToken === result.offerToken
      && item.source === 'app' && item.entitlement?.level === 'product')
      ? campaignLabel({...result, discountCode:null}, entries) : '';
  }
  function cartBenefit(cart, native, row, entries = [], entitlements = []) {
    const source = native?.items?.find(item => item.key === row.key);
    if (!source || String(source.variant_id) !== row.variantId || source.quantity !== row.quantity
      || native.items.filter(item => String(item.variant_id) === row.variantId).length !== 1) return null;
    const matches = cart?.items?.filter(item => String(item.product?.variantId) === row.variantId) || [];
    if (matches.length !== 1) return null;
    const item = matches[0], unit = item.price?.product, line = item.price?.line;
    if (!unit || !line || item.isSubscriptionProduct || item.isGift || item.product.sellingPlanId != null
      || unit.compareAt > unit.original || line.original !== unit.original * row.quantity
      || item.discount?.line?.amount !== line.original - line.discounted) return null;
    if (source.original_price !== unit.original || source.final_price !== unit.discounted
      || source.original_line_price !== line.original || source.final_line_price !== line.discounted) return null;
    const percent = percentage(line.original, line.discounted, item.discount.line.percentage);
    if (percent === null) return null;
    // Shopify allocations are the authoritative identity when the app's public
    // discounted-line payload omits its documented discountCode property.
    const allocations = source.line_level_discount_allocations || [];
    const codes = allocations.filter(a => a.amount > 0 && a.discount_application?.type === 'discount_code');
    const allocatedCode = source.final_line_price === line.discounted
      && codes.reduce((sum, a) => sum + a.amount, 0) === line.original - line.discounted
      ? [...new Set(codes.map(a => a.discount_application.title))].join(' + ') : '';
    // Initial DN cart data can omit the allocation type; accept that shape only
    // when its exact offer is also confirmed as an applied app entitlement.
    const appliedApp = entitlements.some(e => e.offerToken === item.appliedPromotion?.offerToken
      && e.source === 'app' && e.applied === true && e.entitlement?.level === 'product');
    const automatic = allocations.length > 0 && allocations.every(a => a.discount_application?.type === 'automatic'
      || (a.discount_application?.type === '' && appliedApp))
      && allocations.reduce((sum, a) => sum + a.amount, 0) === line.original - line.discounted;
    return { percentage: percent, saving: line.original - line.discounted,
      original: unit.original, discounted: unit.discounted, lineOriginal: line.original,
      lineDiscounted: line.discounted, label: allocatedCode || (automatic ? campaignLabel(item.appliedPromotion, entries) : '') };
  }
  function money(cents, currency, locale) {
    return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
  }
  function render(host, model) {
    if (!model) { host.replaceChildren(); return; }
    const doc = host.ownerDocument;
    const template = doc.getElementById('cg-discount-benefit-template');
    if (!template) return;
    const currency = model.currency || template.dataset.currency;
    const text = template.dataset.badge.replace('__percentage__', String(model.percentage));
    const label = model.label ? model.label + ' · ' + text : text;
    const savings = template.dataset.savings.replace('__amount__', money(model.saving, currency, doc.documentElement.lang));
    if (host.querySelector('.cg-discount-benefit__label')?.textContent === label
      && host.querySelector('.cg-discount-benefit__saving')?.textContent === savings) return;
    const fragment = template.content.cloneNode(true);
    fragment.querySelector('.cg-discount-benefit__label').textContent = label;
    fragment.querySelector('.cg-discount-benefit__saving').textContent = savings;
    host.replaceChildren(fragment);
  }
  function mountCart(doc) {
    const view = doc.defaultView;
    const entries = campaignEntries(doc);
    const reservedVariants = new Set();
    let latest = null, frame = 0, discounted = false, processed = false;
    function clear(row) {
      row.classList.remove('cg-discount-benefit--active');
      const host = row.querySelector('[data-cg-cart-benefit]');
      if (host?.childNodes.length) host.replaceChildren();
      row.querySelectorAll('[data-cg-original-price]').forEach(node => node.remove());
    }
    function reconcile() {
      frame = 0;
      const native = view.discountNinja?.api?.cart?.content;
      // DN can finish a cart with updated + processed only.
      // Require matching line identities and amounts before accepting that path.
      const confirmed = processed && Array.isArray(latest?.items) && Array.isArray(native?.items)
        && latest.items.length === native.items.length && native.items.every(source => {
          const matches = latest.items.filter(item => item.product?.id === source.key);
          const price = matches.length === 1 ? matches[0].price : null;
          return Number.isSafeInteger(source.final_price) && Number.isSafeInteger(source.final_line_price)
            && price?.product?.discounted === source.final_price && price?.product?.original === source.original_price
            && price?.line?.discounted === source.final_line_price && price?.line?.original === source.original_line_price;
        });
      let ready = (discounted || confirmed) && Array.isArray(latest?.items) && Array.isArray(native?.items)
        && latest.items.length === native.items.length;
      doc.querySelectorAll('.cg-dn-price--active[data-cg-dn-variant]').forEach(price => {
        reservedVariants.add(price.dataset.cgDnVariant);
      });
      doc.querySelectorAll('[data-la-dn-cart-item-row]').forEach(row => {
        const host = row.querySelector('[data-cg-cart-benefit]');
        if (!host) return;
        const visible = row.getClientRects().length > 0 && view.getComputedStyle(row).visibility !== 'hidden';
        // Layout only: a newly added row can precede its cart discount response.
        // Never reuse the PDP amount or label as a cart discount confirmation.
        host.toggleAttribute('data-cg-cart-reserve-benefit', reservedVariants.has(host.dataset.variant));
        const model = cartBenefit(latest, native, {key:row.dataset.laDnCartItemKey,
          variantId:host.dataset.variant, quantity:Number(host.dataset.quantity)}, entries, view.discountNinja?.api?.entitlements || []);
        const source = native?.items?.find(item => item.key === row.dataset.laDnCartItemKey);
        if (visible && (!source || source.quantity !== Number(host.dataset.quantity))) ready = false;
        const priceHost = row.querySelector('[data-la-dn-cart-item-product-price]');
        const price = priceHost?.querySelector('[data-la-dn-shortcode-product="DISCOUNTED_PRICE"]');
        const amount = price?.querySelector('[data-limoniapps-discountninja-price-cents-presentmentcurrency]');
        if (!model || !amount || Number(amount.getAttribute('data-limoniapps-discountninja-price-cents-presentmentcurrency')) !== model.discounted) {
          if (visible && ((amount && Number(amount.getAttribute('data-limoniapps-discountninja-price-cents-presentmentcurrency')) !== source?.final_price)
            || (!amount && source?.final_price < source?.original_price
              && !(priceHost?.childNodes.length === 0 && Number(host.dataset.cgCartPrice) === source.final_price
                && Number(host.dataset.cgCartLinePrice) === source.final_line_price)))) ready = false;
          clear(row); return;
        }
        render(host, {...model,currency:latest.currency});
        if (!host.childNodes.length) return;
        reservedVariants.add(host.dataset.variant);
        host.setAttribute('data-cg-cart-reserve-benefit', '');
        row.classList.add('cg-discount-benefit--active');
        // Drawer settings may use Replace mode. Add only the confirmed original;
        // the app continues to own its price outlet and all payable amounts.
        const nativeOriginal = priceHost.querySelector('[data-la-dn-shortcode-product="ORIGINAL_PRICE,STRIKETHROUGH"]');
        const originalAmount = nativeOriginal?.querySelector('[data-limoniapps-discountninja-price-cents-presentmentcurrency]');
        if (originalAmount && Number(originalAmount.getAttribute('data-limoniapps-discountninja-price-cents-presentmentcurrency')) === model.original) {
          const text = money(model.original, latest.currency, doc.documentElement.lang);
          const leaf = originalAmount.querySelector('.money') || originalAmount;
          if (leaf.textContent !== text) leaf.textContent = text;
        }
        const priceWrapper = row.querySelector('.cg-cart-price');
        let original = priceWrapper?.querySelector(':scope > [data-cg-original-price]');
        if (nativeOriginal || !row.matches('.xo-cart-mini-item')) original?.remove();
        else if (priceWrapper) {
          if (!original) { original = doc.createElement('s'); original.dataset.cgOriginalPrice = ''; priceHost.before(original); }
          const text = money(model.original, latest.currency, doc.documentElement.lang);
          if (original.textContent !== text) original.textContent = text;
        }
      });
      if (ready) {
        discounted = false; processed = false;
        doc.dispatchEvent(new view.Event('cg:cart-pricing:ready'));
      }
    }
    function schedule() { if (!frame) frame = view.requestAnimationFrame(reconcile); }
    function updated(event) { latest = event.detail?.data?.[0] || null; discounted = event.type === 'la:dn:cart:discounted'; schedule(); }
    function invalidate() { latest = null; discounted = false; processed = false; doc.querySelectorAll('[data-la-dn-cart-item-row]').forEach(clear); }
    doc.addEventListener('la:dn:cart:updated', updated);
    doc.addEventListener('la:dn:cart:discounted', updated);
    doc.addEventListener('la:dn:cart-mutations:processing', invalidate);
    doc.addEventListener('la:dn:cart-mutations:processed', () => { processed = true; schedule(); });
    view.addEventListener('pageshow', event => { if (event.persisted) invalidate(); });
    new view.MutationObserver(schedule).observe(doc.documentElement, {childList:true,subtree:true});
  }
  const api = {percentage,campaignLabel,campaignEntries,pdpLabel,cartBenefit,money,render,mountCart};
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  if (window.CgDiscountBenefit) return;
  window.CgDiscountBenefit = api;
  mountCart(document);
})();
